/**
 * Themes.
 *
 * Every colour in the app comes from a token, so a theme is nothing but a set
 * of token values — the CSS never changes. The actual values live in
 * theme/themes.css under `:root[data-theme="…"]`; this file is the registry
 * the UI reads to build the picker.
 *
 * Swatches here are only for the picker chips; they are not the source of
 * truth for the theme itself.
 */
export interface ThemeDef {
  id: string;
  name: string;
  /** [background, panel, accent] — the three chips in the picker. */
  swatch: [string, string, string];
  /** Tells the browser to render form controls and scrollbars to match. */
  scheme: 'dark' | 'light';
}

export const THEMES: ThemeDef[] = [
  {
    id: 'midnight',
    name: 'Midnight',
    swatch: ['#0f1115', '#161a22', '#4c8dff'],
    scheme: 'dark',
  },
  {
    id: 'plex',
    name: 'Classic',
    swatch: ['#1f1f1f', '#282828', '#e5a00d'],
    scheme: 'dark',
  },
  {
    id: 'reds',
    name: 'Red & Black',
    swatch: ['#0a0a0a', '#151515', '#c6011f'],
    scheme: 'dark',
  },
  {
    id: 'daylight',
    name: 'Daylight',
    swatch: ['#f6f7f9', '#ffffff', '#2563eb'],
    scheme: 'light',
  },
];

/*
 * THE DEFAULT IS THE PLEX LOOK, and that is a decision about competition
 * rather than taste.
 *
 * Everybody in this household is already on Plex and happy with it. An app
 * that asks them to switch does not merely have to look good — it has to not
 * feel like a downgrade in the first ten seconds, because the alternative is
 * one button away and already works. Starting in a palette they recognise
 * removes "this looks like something else" from the list of reasons to go
 * back, and the other themes are still there for anybody who wants one.
 */
export const DEFAULT_THEME = 'plex';
export const THEME_STORAGE_KEY = 'n8m.theme';

export function isKnownTheme(id: string | null): boolean {
  return !!id && THEMES.some((t) => t.id === id);
}
